import { keepPreviousData } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Eye, Pencil, Plus } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import {
  Badge,
  Button,
  buttonVariants,
  Card,
  Combobox,
  Pagination,
  Skeleton,
  TBody,
  Table,
  Td,
  Th,
  THead,
  Tr,
} from "@rumbledirect/ui-v2";
import type { VehicleConversion } from "@/configurator/vehicleConversions/vehicleConversion";
import { toVehicleConversionCreateInput } from "@/configurator/vehicleConversions/vehicleConversionForm";
import { VehicleConversionSheet } from "@/configurator/vehicleConversions/VehicleConversionSheet";
import { useCreateVehicleConversion } from "@/configurator/vehicleConversions/useCreateVehicleConversion";
import { useVehicleTypeOptions } from "@/configurator/vehicleConversions/useVehicleTypeOptions";
import { RequirePermission } from "@/currentUser/RequirePermission";
import { useGetAdminConfiguratorVehicleConversions } from "@/lib/api/generated/configurator/configurator";
import { TopbarActions } from "@/shell/TopbarActions";

const CREATE_PERMISSION = "admin_configurator_vehicle_conversion_create";
const UPDATE_PERMISSION = "admin_configurator_vehicle_conversion_update";

const ITEMS_PER_PAGE = 20;
const SKELETON_ROW_KEYS = Array.from(
  { length: 5 },
  (_, index) => `vehicle-conversion-skeleton-${index}`,
);

interface VehicleConversionsSearch {
  readonly page: number;
  readonly vehicleType?: string;
}

export const Route = createFileRoute(
  "/_authenticated/configurator/vehicle-conversions",
)({
  validateSearch: (
    search: Record<string, unknown>,
  ): VehicleConversionsSearch => {
    const page = Number(search.page);
    const vehicleType =
      typeof search.vehicleType === "string" && search.vehicleType
        ? search.vehicleType
        : undefined;
    return {
      page: Number.isInteger(page) && page >= 1 ? page : 1,
      vehicleType,
    };
  },
  component: VehicleConversionsPage,
});

function VehicleConversionsPage(): ReactNode {
  const { page, vehicleType } = Route.useSearch();
  const navigate = Route.useNavigate();
  const routerNavigate = useNavigate();

  const { options: vehicleTypeOptions, isPending: vehicleTypesPending } =
    useVehicleTypeOptions();
  const vehicleTypeLabels = useMemo(
    () =>
      new Map(vehicleTypeOptions.map((option) => [option.value, option.label])),
    [vehicleTypeOptions],
  );

  const { data, isPending, isPlaceholderData, isError, refetch } =
    useGetAdminConfiguratorVehicleConversions(
      { page, vehicleType },
      { query: { placeholderData: keepPreviousData } },
    );

  const [createOpen, setCreateOpen] = useState(false);
  const createMutation = useCreateVehicleConversion();

  const totalItems = data?.totalItems ?? 0;
  const members = data?.member;

  const openEdit = (id: string) =>
    void routerNavigate({
      to: "/configurator/vehicle-conversions/$vehicleConversionId/edit",
      params: { vehicleConversionId: id },
      search: { tab: "algemeen" },
    });

  return (
    <div className="flex flex-col gap-5">
      <TopbarActions>
        <RequirePermission permission={CREATE_PERMISSION}>
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" aria-hidden="true" />
            Nieuwe conversie
          </Button>
        </RequirePermission>
      </TopbarActions>

      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          Voertuigconversies
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Beheer de voertuigconversies die de configurator aanbiedt, inclusief
          inrichting, opties en uitsluitingen.
        </p>
      </div>

      <div className="w-full max-w-xs">
        <Combobox
          aria-label="Filter op voertuigtype"
          placeholder="Alle voertuigtypen"
          value={vehicleType}
          onValueChange={(value) =>
            void navigate({
              search: (prev) => ({
                ...prev,
                page: 1,
                vehicleType: value ?? undefined,
              }),
            })
          }
          options={vehicleTypeOptions}
          loading={vehicleTypesPending}
          clearable
          fluid
        />
      </div>

      <Card className="overflow-hidden">
        <Table>
          <THead>
            <Tr>
              <Th>Titel</Th>
              <Th>Voertuigtype</Th>
              <Th className="w-28">Status</Th>
              <Th className="w-40 text-right">
                <span className="sr-only">Acties</span>
              </Th>
            </Tr>
          </THead>
          <TBody
            aria-busy={isPlaceholderData}
            className={isPlaceholderData ? "opacity-60" : undefined}
          >
            <VehicleConversionRows
              isPending={isPending}
              isError={isError}
              members={members}
              vehicleTypeLabels={vehicleTypeLabels}
              updatePermission={UPDATE_PERMISSION}
              onEdit={openEdit}
              onRetry={() => void refetch()}
            />
          </TBody>
        </Table>
      </Card>

      {totalItems > ITEMS_PER_PAGE && (
        <Pagination
          page={page}
          pageSize={ITEMS_PER_PAGE}
          totalItems={totalItems}
          status={isPending ? "pending" : isError ? "error" : "ready"}
          onPageChange={(next) =>
            void navigate({ search: (prev) => ({ ...prev, page: next }) })
          }
        />
      )}

      <VehicleConversionSheet
        open={createOpen}
        onOpenChange={setCreateOpen}
        isSubmitting={createMutation.isPending}
        onSubmit={(values) => {
          createMutation.mutate(toVehicleConversionCreateInput(values), {
            onSuccess: (created) => {
              setCreateOpen(false);
              if (created.id) openEdit(created.id);
            },
          });
        }}
      />
    </div>
  );
}

function VehicleConversionRows({
  isPending,
  isError,
  members,
  vehicleTypeLabels,
  updatePermission,
  onEdit,
  onRetry,
}: {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly members: readonly VehicleConversion[] | undefined;
  readonly vehicleTypeLabels: ReadonlyMap<string, string>;
  readonly updatePermission: string;
  readonly onEdit: (id: string) => void;
  readonly onRetry: () => void;
}): ReactNode {
  if (isPending) {
    return SKELETON_ROW_KEYS.map((key) => (
      <Tr key={key}>
        <Td>
          <Skeleton className="h-4 w-40" />
        </Td>
        <Td>
          <Skeleton className="h-4 w-32" />
        </Td>
        <Td>
          <Skeleton className="h-5 w-16 rounded-full" />
        </Td>
        <Td>
          <div className="flex justify-end gap-1">
            <Skeleton className="h-8 w-8" />
            <Skeleton className="h-8 w-8" />
          </div>
        </Td>
      </Tr>
    ));
  }

  if (isError) {
    return (
      <Tr>
        <Td colSpan={4} className="py-10 text-center">
          <div className="space-y-3">
            <p className="text-muted-foreground">
              We konden de voertuigconversies niet laden.
            </p>
            <Button variant="outline" size="sm" onClick={onRetry}>
              Opnieuw proberen
            </Button>
          </div>
        </Td>
      </Tr>
    );
  }

  if (members === undefined || members.length === 0) {
    return (
      <Tr>
        <Td colSpan={4} className="py-10 text-center text-muted-foreground">
          Nog geen voertuigconversies.
        </Td>
      </Tr>
    );
  }

  return members.map((conversion) => {
    const id = conversion.id;
    const label = conversion.title;
    return (
      <Tr key={conversion.id}>
        <Td className="font-medium">
          {id ? (
            <Link
              to="/configurator/vehicle-conversions/$vehicleConversionId"
              params={{ vehicleConversionId: id }}
              search={{ tab: "algemeen" }}
              className="text-foreground underline-offset-4 hover:underline"
            >
              {label}
            </Link>
          ) : (
            label
          )}
        </Td>
        <Td>
          {conversion.vehicleType
            ? (vehicleTypeLabels.get(conversion.vehicleType) ?? "—")
            : "—"}
        </Td>
        <Td>
          {conversion.isArchived ? (
            <Badge variant="neutral" size="sm">
              Gearchiveerd
            </Badge>
          ) : (
            <Badge variant="success" size="sm">
              Actief
            </Badge>
          )}
        </Td>
        <Td>
          <div className="flex justify-end gap-1">
            {id && (
              <Link
                to="/configurator/vehicle-conversions/$vehicleConversionId"
                params={{ vehicleConversionId: id }}
                search={{ tab: "algemeen" }}
                className={buttonVariants({
                  variant: "ghost",
                  size: "icon-sm",
                })}
                aria-label={`${label} bekijken`}
              >
                <Eye className="size-4" aria-hidden="true" />
              </Link>
            )}
            <RequirePermission permission={updatePermission}>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={`${label} bewerken`}
                onClick={() => id && onEdit(id)}
              >
                <Pencil className="size-4" aria-hidden="true" />
              </Button>
            </RequirePermission>
          </div>
        </Td>
      </Tr>
    );
  });
}
